import React from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
} from "@mui/material";
import Map from "../../../Map";
import ProjectSizeButtonGroup from "../../../ProjectSizeButtonGroup";
import ProjectBoundingBoxLayer from "../../../layer_components/ProjectBoundingBoxLayer";
import { BulkStepProps, AspectRatioKey, ProjectSize } from "../../types";
import { PROJECT_SIZE_METERS } from "../../../../constants/project";
import { ProjectBoundary } from "../../../../types/project";

const bboxToPolygon = (top: number, left: number, bottom: number, right: number): ProjectBoundary => ({
  type: "Polygon",
  coordinates: [[[left, bottom], [right, bottom], [right, top], [left, top], [left, bottom]]],
});

const BulkProjectSizeStep: React.FC<BulkStepProps> = ({
  data,
  onUpdateData,
}) => {

  const handleProjectSizeChange = (size: string) => {
    onUpdateData({ projectSize: size as ProjectSize });
  };

  const handleAspectRatioChange = (aspectRatio: string) => {
    if (aspectRatio === "1:1" || aspectRatio === "2:1" || aspectRatio === "1:2") {
      onUpdateData({ projectAspectRatio: aspectRatio as AspectRatioKey });
    }
  };

  const bufferLatLng = (
    lat: number,
    lng: number,
    bufferMetersY: number,
    bufferMetersX: number
  ) => {
    const earthRadius = 6371000;
    const latBuffer = (bufferMetersY / earthRadius) * (180 / Math.PI);
    const lngBuffer = (bufferMetersX / earthRadius) * (180 / Math.PI) / Math.cos(lat * Math.PI / 180);
    
    return {
      top: lat + latBuffer,
      bottom: lat - latBuffer,
      left: lng - lngBuffer,
      right: lng + lngBuffer,
    };
  };

  // Calculate bounding boxes for all projects
  const calculateProjectBoundingBoxes = () => {
    if (data.parsedProjects.length === 0) return [];

    const { x: bufferXInit, y: bufferYInit } = PROJECT_SIZE_METERS[data.projectSize];
    let bufferX = bufferXInit;
    let bufferY = bufferYInit;
    
    // Adjust for aspect ratio
    if (data.projectAspectRatio === "2:1") {
      bufferX *= 2;
    } else if (data.projectAspectRatio === "1:2") {
      bufferY *= 2;
    }

    return data.parsedProjects.map((project, index) => {
      const bbox = bufferLatLng(project.lat, project.lng, bufferY, bufferX);
      return {
        ...bbox,
        project,
        index
      };
    });
  };

  const projectBoundingBoxes = calculateProjectBoundingBoxes();

  // Create map layers for pins and bounding boxes
  const pinLayers = data.parsedProjects.map((project, index) => ({
    id: `pin-${index}`,
    name: project.name,
    source: {
      type: "geojson" as const,
      data: {
        type: "FeatureCollection" as const,
        features: [{
          type: "Feature" as const,
          geometry: {
            type: "Point" as const,
            coordinates: [project.lng, project.lat]
          },
          properties: {
            name: project.name
          }
        }]
      }
    },
    layerStyle: {
      id: `pin-${index}`,
      type: "circle" as const,
      paint: {
        "circle-radius": 8,
        "circle-color": "#00d4ad",
        "circle-stroke-color": "white",
        "circle-stroke-width": 2,
      },
    },
    toolbarButton: null,
    active: true,
    stale: false,
    setStale: () => {},
  }));

  const boundingBoxLayers = projectBoundingBoxes.map((bbox, index) =>
    ProjectBoundingBoxLayer(
      `bbox-bulk-${index}`,
      bbox.project.name,
      bboxToPolygon(bbox.top, bbox.left, bbox.bottom, bbox.right),
      true, // showName
      false  // fill
    )
  );


  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <Box sx={{ p: 3, backgroundColor: "#f8f9fa", borderBottom: "1px solid #e0e0e0" }}>
        <Typography
          variant="h5"
          sx={{
            mb: 1,
            fontWeight: 600,
            color: "#1a1a1a",
          }}
        >
          Project Size & Scope
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "#666",
          }}
        >
          Configure the size and aspect ratio for all {data.parsedProjects.length} projects
        </Typography>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>
        {/* Left Panel - Controls */}
        <Box sx={{ 
          width: "35%",
          p: 3, 
          backgroundColor: "#f8f9fa",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
          overflow: "auto"
        }}>
          <Typography
            variant="h6"
            sx={{
              mb: 2,
              fontWeight: 600,
              color: "#1a1a1a",
              textAlign: "center",
            }}
          >
            Project Configuration
          </Typography>
          <Typography
            variant="body2"
            sx={{
              mb: 4,
              color: "#666",
              textAlign: "center",
            }}
          >
            These settings will be applied to all {data.parsedProjects.length} projects
          </Typography>

          {/* Project Size */}
          <Box sx={{ mb: 4, width: "100%" }}>
            <Typography
              variant="subtitle1"
              sx={{
                mb: 3,
                fontWeight: 600,
                color: "#1a1a1a",
                textAlign: "center",
              }}
            >
              Project Size
            </Typography>
            <Card sx={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
              <CardContent sx={{ p: 3 }}>
                <ProjectSizeButtonGroup
                  projectSize={data.projectSize}
                  onProjectSizeChange={handleProjectSizeChange}
                  projectAspectRatio={data.projectAspectRatio}
                  onProjectAspectRatioChange={handleAspectRatioChange}
                />
              </CardContent>
            </Card>
          </Box>

          {/* Project Summary */}
          <Box sx={{ width: "100%" }}>
            <Typography
              variant="subtitle1"
              sx={{
                mb: 2,
                fontWeight: 600,
                color: "#1a1a1a",
                textAlign: "center",
              }}
            >
              Bulk Creation Summary
            </Typography>
            <Card sx={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ 
                  textAlign: "center",
                  p: 2,
                  backgroundColor: "#e8f5e8",
                  borderRadius: 2,
                  border: "1px solid #00d4ad",
                  mb: 2
                }}>
                  <Typography 
                    variant="h4" 
                    sx={{ 
                      fontWeight: 600,
                      color: "#00b89c",
                      mb: 1
                    }}
                  >
                    {data.parsedProjects.length}
                  </Typography>
                  <Typography variant="body1" sx={{ color: "#666" }}>
                    Projects to be created
                  </Typography>
                </Box>

                <Box sx={{ mt: 2, textAlign: "left" }}>
                  <Typography variant="body2" sx={{ mb: 1, color: "#666" }}>
                    <strong>Size:</strong> {data.projectSize}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1, color: "#666" }}>
                    <strong>Aspect Ratio:</strong> {data.projectAspectRatio}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#666" }}>
                    <strong>Dimensions:</strong> {PROJECT_SIZE_METERS[data.projectSize].x}m × {PROJECT_SIZE_METERS[data.projectSize].y}m
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* Right Panel - Map */}
        <Box sx={{ width: "65%", p: 3 }}>
          <Card sx={{ height: "100%", maxHeight: "calc(var(--vh100) - 200px)" }}>
            <CardContent sx={{ height: "100%", p: 0, overflow: "hidden" }}>
              {data.parsedProjects.length > 0 ? (
                <Map
                  layers={[
                    ...pinLayers,
                    ...boundingBoxLayers
                  ]}
                  searchable={false}
                />
              ) : (
                <Box sx={{ 
                  height: "100%", 
                  display: "flex", 
                  alignItems: "center", 
                  justifyContent: "center",
                  backgroundColor: "#f5f5f5"
                }}>
                  <Typography variant="body1" sx={{ color: "#666", textAlign: "center" }}>
                    No projects loaded.<br />
                    Please upload a CSV file first.
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};

export default BulkProjectSizeStep;
